import './DocumentUpload.scss'
import Store from 'state/Store';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { ReactComponent as PaperClip } from 'assets/paperclip.svg';
import { getDeceasedDetails } from 'state/deceasedDetails/DeceasedDetailsSelectors';
import { getDeceasedDetailsEnabled } from 'state/deceasedDetailsEnabled/DeceasedDetailsEnabledSelectors';
import { setDeceasedDetails } from 'state/deceasedDetails/DeceasedDetailsActions';
import { setDeceasedDetailsEnabled } from 'state/deceasedDetailsEnabled/DeceasedDetailsEnabledActions';
import {
  Content
} from 'utils/Content';
import {
  setCanContinueFalse,
  setCanContinueTrue,
} from 'state/canContinue/CanContinueActions';
import {
  acceptableFileTypesArray,
  validateFile
} from 'utils/FileUtils';
import React, {
  useEffect,
  useState,
  useRef
} from 'react';
import WithPermissions from 'components/shared/WithPermissions';
import { LFEBeaconDatePicker, LFEBeaconRadioGroup, LFEBeaconRadioButton } from 'components/shared/LFEBeaconComponents';
import { validateUploadedFile } from 'utils/Rest';
import { getUploadFileDetails } from 'state/uploadFile/UploadFileSelectors';
import { isValidDOB } from 'utils/Validation';

const DocumentUpload = ({ deceasedDetails, deceasedDetailsEnabled, uploadFileDetails }) => {
  const { dDOD, dDeathCert } = deceasedDetails
  const { enableDeathCert } = deceasedDetailsEnabled
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef();

  const radioOnChange = (id, event) =>{
    if (event && event.target && id) {
      if (id === 'enableDeathCert') {
        Store.dispatch(setDeceasedDetailsEnabled('enableDeathCert', !enableDeathCert));
      }
    }
  }
  
  useEffect(() => {
    if(uploadFileDetails && uploadFileDetails.error && uploadFileDetails.error.message) {
      Store.dispatch(setDeceasedDetails('dDeathCert', null))
      uploadFileDetails.error?.message && alert(uploadFileDetails.error.message)
      reset();
    }
    if(uploadFileDetails && uploadFileDetails.results && uploadFileDetails.results.fileName && !dDeathCert) {
      Store.dispatch(setDeceasedDetails('dDeathCert', uploadFile))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileDetails])

  useEffect(()=>{
    if ((enableDeathCert !== null && (enableDeathCert && dDeathCert)) || (!enableDeathCert && (isValidDOB(dDOD) || isEmpty(dDOD)))){
      Store.dispatch(setCanContinueTrue())
    }else{
      Store.dispatch(setCanContinueFalse())
    }
  },[deceasedDetailsEnabled, deceasedDetails, enableDeathCert, dDeathCert, dDOD])

  const reset = () => {
    fileInputRef.current.value = '';
    setUploadFile(null);
    Store.dispatch(setDeceasedDetails('dDeathCert',null));
  }

  const handleSelectFile = (event) => {
    const file = event.target.files[0];
    const isValid = validateFile(file);
    if (isValid){
      setUploadFile(file);
      validateUploadedFile(file);
    }
  }
  
  return (
      <div className="dn-document-upload">
        <p data-testid="DocumentUploadIntro"
           className="dn-page-text-intro">{ Content.DocumentUploadIntro }</p>
        <LFEBeaconRadioGroup id="enableDeathCert"
                             className="death-cert-radio-object">
          <LFEBeaconRadioButton name="death-cert-radio"
                                type="radio"
                                data-testid="deathCertYesTitle"
                                id="enableDeathCertYes"
                                checked={enableDeathCert}
                                onBeaconChange={(event) => radioOnChange('enableDeathCert', event)}
                                label="Yes, I have the death certificate">
          </LFEBeaconRadioButton>
          <LFEBeaconRadioButton name="death-cert-radio"
                                type="radio"
                                data-testid="deathCertNoTitle"
                                id="enableDeathCertNo"
                                checked={!enableDeathCert && enableDeathCert !== null}
                                onBeaconChange={(event) => radioOnChange('enableDeathCert', event)}
                                label={Content.DocumentUploadNoText}>
          </LFEBeaconRadioButton>
        </LFEBeaconRadioGroup>
        {enableDeathCert  && <DeathCertYes dDeathCert={dDeathCert}
                                           uploadFile={uploadFile}
                                           reset={reset}
                                           handleSelectFile={handleSelectFile}
                                           fileInputRef={fileInputRef}
                                           enableDeathCert={enableDeathCert}/>}
        {!enableDeathCert && enableDeathCert !== null && <DeathCertNo dDOD={dDOD}/>}
      </div>
  );
};

const ReviewDetailsBlockName = ({ data, testid, className }) => (
  <span data-testid={testid}
        className={className}>
    {data}
  </span>
);

const ReviewDetailsDeathCertificatePermissions = WithPermissions(ReviewDetailsBlockName);

const DeathCertYes = ({ dDeathCert, fileInputRef, reset, handleSelectFile })=>{

  //Have to keep the input element rendered on the screen to access its value in case of reset
  const inputStyle = dDeathCert ? { 'display': 'none' } : {};

  return(
      <div className="death-cert-yes">
        { !dDeathCert && 
        <p data-testid="death-cert-yes-text"
           className="death-cert-yes-text">
          If you don’t have a digital version, you may wish to take a well-lit photo of the death certificate and email yourself a copy that you can upload here.
        </p>}
        <p data-testid="accepted-file"
           className="accepted-file-text">Accepted file types: PDF, PNG, JPEG, TIF up to 5MB in size. (no password protected files)</p>
        {dDeathCert && <div className="death-cert-file-group">
          <span className="death-cert-svg">
          <PaperClip/>
          <ReviewDetailsDeathCertificatePermissions data={dDeathCert.name}
                                                    testid="death-cert-file-name"
                                                    className="death-cert-file-name"
                                                    shouldMask
          />
          </span>
          <button className="remove-button"
                  onClick={() => reset() }>REMOVE</button>
        </div>}
        <label className="file-drag-drop"
               style={inputStyle}>
          <input className="drag-file-input"
                 id="file-input"
                 data-testid="dragDropFileUpload"
                 type="file"
                 ref={fileInputRef}
                 onChange={(e) => handleSelectFile(e)}
                 accept={acceptableFileTypesArray} />
          <span data-testid="drag-drop-text"
                className="drag-drop-text">Click to browse for a file, or drag and drop your your file here</span>
        </label>
        </div>
  )
}

const DeathCertNo = ({ dDOD })=>{
  const handleOnChange = (id,event)=>{
    if (event && event.target && id=== 'dDOD') {
      Store.dispatch(setDeceasedDetails(id, event.target.value))
    }
  }


  return(
      <div className="death-cert-no">
        <p className="death-cert-no-text">If you don’t have the death certificate now, you can provide it later.</p>
        <div className="deceased-DOD-info">
          <LFEBeaconDatePicker className='dDOD-picker-dDetails blocked_data'
                               id ='dDOD'
                               label='Date of death (optional)'
                               value={dDOD}
                               required={false}
                               onBeaconInput={(event) => handleOnChange('dDOD', event)}
                               max={moment().format('YYYY-MM-DD')}/>
        </div>
        <p className="death-cert-no-text">If you don’t know the date of death, you can leave this blank and continue to next steps.</p>
      </div>
  )
}

const mapStateToProps = state => ({
  deceasedDetailsEnabled: getDeceasedDetailsEnabled(state),
  deceasedDetails: getDeceasedDetails(state),
  uploadFileDetails: getUploadFileDetails(state)
});
export default connect(mapStateToProps)(DocumentUpload)